import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  fetchBookingByNumber,
  fetchSiblingDepartures,
} from '../lib/bookings';
import { formatError } from '../lib/errors';
import type { AuditLogRow, BookingRow, LifecycleStatus } from '../types';
import { LifecyclePill, PaymentPill } from '../components/StatusPill';
import { CheckinButton } from '../components/CheckinButton';
import { CancelRefundModal } from '../components/CancelRefundModal';
import { EditBookingModal } from '../components/EditBookingModal';
import { MarkPaidModal } from '../components/MarkPaidModal';
import { TerminalChargeModal } from '../components/TerminalChargeModal';
import { AuditTimeline } from '../components/AuditTimeline';
import { formatVenueDateTime } from '../lib/time';
import { bookingDisplayName } from '../lib/guest-name';
import { useIsSingleRoomByRoomId } from '../lib/room-counts';
import {
  resendBookingEmail,
  type ResendEmailAction,
} from '../api/resend-email';

export function BookingDetailScreen() {
  const { number } = useParams<{ number: string }>();
  const [booking, setBooking] = useState<BookingRow | null>(null);
  const [audit, setAudit] = useState<AuditLogRow[]>([]);
  // Sibling departures = the OTHER departures under this departure's
  // parent booking. Always [] for single-departure bookings (the
  // common case); populated for multi-departure carts.
  const [siblings, setSiblings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [terminalOpen, setTerminalOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState<ResendEmailAction | null>(null);
  const [resendNotice, setResendNotice] = useState<{
    kind: 'ok' | 'err';
    message: string;
  } | null>(null);

  const handleResend = useCallback(
    async (action: ResendEmailAction, bookingId: string) => {
      setResendBusy(action);
      setResendNotice(null);
      const result = await resendBookingEmail({ booking_id: bookingId, action });
      setResendBusy(null);
      if (!result.ok || !result.data) {
        const msg =
          result.error?.message ??
          result.error?.error ??
          `HTTP ${result.status}`;
        setResendNotice({ kind: 'err', message: msg });
        return;
      }
      const okMessage =
        action === 'manage_link'
          ? 'Verzet-link verstuurd.'
          : action === 'payment_request'
            ? 'Betaallink verstuurd.'
            : result.data.rotated
              ? 'Bevestiging verstuurd. Oude QR / verzet-link is vervangen.'
              : 'Bevestiging verstuurd.';
      setResendNotice({ kind: 'ok', message: okMessage });
      void load();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Part of this departure paid with a physical gift card (not in Totaal,
  // which is the cash amount).
  const [giftCardCents, setGiftCardCents] = useState(0);
  const bookingIdForCard = booking?.id ?? null;
  useEffect(() => {
    if (!bookingIdForCard) return;
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .schema('booking')
        .from('departure_experiences')
        .select('gift_card_cents')
        .eq('departure_id', bookingIdForCard);
      if (!cancelled && data) {
        setGiftCardCents((data as { gift_card_cents: number | null }[]).reduce((n, r) => n + (r.gift_card_cents ?? 0), 0));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingIdForCard]);

  const load = useCallback(async () => {
    if (!number) return;
    setError(null);
    try {
      const row = await fetchBookingByNumber(number);
      if (!row) {
        setError('Booking not found');
        setLoading(false);
        return;
      }
      setBooking(row);

      // audit_log.booking_id is polymorphic — events created BEFORE the
      // multi-experience EXPAND were keyed on bookings.id, events created
      // AFTER are keyed on the departure.id for per-orderline events and
      // on bookings.id for parent-level events (e.g. discount redemption).
      // Querying both surfaces every event for this departure correctly.
      const auditIds = [row.id];
      if (row.parent_booking_id && row.parent_booking_id !== row.id) {
        auditIds.push(row.parent_booking_id);
      }
      const { data: logRows, error: lErr } = await supabase
        .schema('booking')
        .from('audit_log')
        .select('id, actor_type, actor_label, action, from_value, to_value, metadata, created_at')
        .in('booking_id', auditIds)
        .order('created_at', { ascending: false })
        .limit(200);

      if (lErr) throw lErr;
      setAudit((logRows ?? []) as AuditLogRow[]);

      // Load siblings under the same parent booking — empty array for
      // single-departure bookings is the common case. Best-effort: on
      // error we just hide the section rather than fail the whole
      // detail load.
      if (row.parent_booking_id) {
        try {
          const sibs = await fetchSiblingDepartures(
            row.parent_booking_id,
            row.id,
          );
          setSiblings(sibs);
        } catch {
          setSiblings([]);
        }
      } else {
        setSiblings([]);
      }
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [number]);

  useEffect(() => {
    void load();
  }, [load]);

  const onLifecycleChange = (next: LifecycleStatus) => {
    setBooking((prev) => (prev ? { ...prev, lifecycle_status: next } : prev));
    void load();
  };

  const isSingleRoom = useIsSingleRoomByRoomId();

  if (loading) return <div className="screen">Laden…</div>;
  if (error || !booking) {
    return (
      <div className="screen">
        <Link to="/today">← Terug</Link>
        <div className="error-card">{error ?? 'Niet gevonden'}</div>
      </div>
    );
  }

  const customer = booking.customers;
  const fullName = bookingDisplayName(booking) || '—';
  // Anonymous counter walk-in: no customer row, so no e-mail to send
  // anything to. Hide the resend buttons and say so in the Klant block.
  const hasCustomer = customer !== null;
  const totalEuros =
    booking.total_cents != null ? (booking.total_cents / 100).toFixed(2) : '—';

  return (
    <div className="screen detail-screen">
      <Link to="/today" className="back-link">← Terug</Link>

      <div className="detail-header">
        <h1>{booking.booking_number}</h1>
        <div className="detail-pills">
          <LifecyclePill status={booking.lifecycle_status} />
          <PaymentPill status={booking.payment_status} />
        </div>
      </div>

      <div className="detail-grid">
        <section>
          <h2>Klant</h2>
          <dl>
            <dt>Naam</dt><dd>{fullName}</dd>
            {hasCustomer ? (
              <>
                <dt>E-mail</dt><dd>{customer.email ?? '—'}</dd>
                <dt>Telefoon</dt><dd>{customer.phone ?? '—'}</dd>
              </>
            ) : (
              <>
                <dt>Contact</dt><dd className="muted">Geen e-mail (balie)</dd>
              </>
            )}
            <dt>Taal</dt><dd>{booking.locale}</dd>
          </dl>
        </section>

        <section>
          <h2>Boeking</h2>
          <dl>
            <dt>Beleving</dt><dd>{booking.experiences?.name ?? '—'}</dd>
            <dt>Personen</dt><dd>{booking.party_size}</dd>
            <dt>Totaal</dt><dd>€ {totalEuros}</dd>
            {giftCardCents > 0 ? (
              <>
                <dt>Cadeaubon</dt><dd>− € {(giftCardCents / 100).toFixed(2)}</dd>
              </>
            ) : null}
            <dt>Aangemaakt</dt><dd>{formatVenueDateTime(booking.created_at)}</dd>
          </dl>
        </section>

        <section>
          <h2>Slots</h2>
          <ul className="slot-list">
            {booking.booking_slots.map((s) => {
              const showRoom = !isSingleRoom(s.room_id);
              return (
                <li key={s.slot_start + s.room_id}>
                  <strong>{formatVenueDateTime(s.slot_start)}</strong>
                  {showRoom ? <> {' · '}{s.rooms?.name ?? '—'}</> : null}
                  {' · '}
                  {s.party_size}p
                </li>
              );
            })}
          </ul>
        </section>

        {booking.notes ? (
          <section>
            <h2>Notities</h2>
            <p className="notes-block">{booking.notes}</p>
          </section>
        ) : null}

        {siblings.length > 0 ? (
          <section className="sibling-departures">
            <h2>Andere vertrekken in deze boeking</h2>
            <p className="muted">
              Deze boeking bevat {siblings.length + 1} vertrekken — elk
              met een eigen QR en bevestigingsmail.
            </p>
            <ul className="sibling-list">
              {siblings.map((sib) => {
                const sibPicks = [...sib.experience_picks].sort(
                  (a, b) => a.pick_index - b.pick_index,
                );
                const sibLabel =
                  sibPicks.length === 0
                    ? (sib.experiences?.name ?? '—')
                    : sibPicks
                        .map(
                          (p) =>
                            `${p.party_size}× ${p.experiences?.name ?? '—'}`,
                        )
                        .join(' + ');
                const firstSlot = sib.booking_slots[0];
                return (
                  <li key={sib.id} className="sibling-row">
                    <Link
                      to={`/booking/${sib.booking_number}`}
                      className="sibling-link"
                    >
                      <strong className="sibling-number">
                        {sib.booking_number}
                      </strong>
                      <span className="sibling-when">
                        {firstSlot
                          ? formatVenueDateTime(firstSlot.slot_start)
                          : '—'}
                      </span>
                      <span className="sibling-exp">{sibLabel}</span>
                      <span className="sibling-party">{sib.party_size}p</span>
                    </Link>
                    <div className="sibling-pills">
                      <LifecyclePill status={sib.lifecycle_status} />
                      <PaymentPill status={sib.payment_status} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      <div className="detail-actions">
        <CheckinButton booking={booking} onChanged={onLifecycleChange} />
        {(booking.lifecycle_status === 'confirmed' ||
          booking.lifecycle_status === 'arrived') ? (
          <>
            <button
              className="edit-trigger-btn"
              onClick={() => setEditOpen(true)}
            >
              {booking.lifecycle_status === 'arrived' ? 'Bewerken (ingecheckt)' : 'Bewerken'}
            </button>
            <button
              className="cancel-trigger-btn"
              onClick={() => setCancelOpen(true)}
            >
              {booking.lifecycle_status === 'arrived' ? 'Boeking annuleren (ingecheckt)' : 'Boeking annuleren'}
            </button>
          </>
        ) : booking.lifecycle_status === 'draft' ? (
          <button
            className="cancel-trigger-btn"
            onClick={() => setCancelOpen(true)}
          >
            Boeking annuleren (concept)
          </button>
        ) : null}
        {booking.payment_status === 'unpaid' &&
          (booking.lifecycle_status === 'confirmed' ||
            booking.lifecycle_status === 'arrived') ? (
          <button
            className="mark-paid-btn"
            onClick={() => setTerminalOpen(true)}
            title="Stuur het openstaande bedrag naar de pinterminal van deze locatie"
          >
            Afrekenen op pinterminal
          </button>
        ) : null}
        {(booking.payment_status === 'unpaid' ||
          booking.payment_status === 'pending' ||
          booking.payment_status === 'failed') ? (
          <button
            className="mark-paid-btn"
            onClick={() => setMarkPaidOpen(true)}
          >
            Markeer als betaald
          </button>
        ) : null}
        {hasCustomer &&
          (booking.lifecycle_status === 'confirmed' ||
            booking.lifecycle_status === 'arrived') ? (
          <>
            <button
              className="resend-email-btn"
              onClick={() => void handleResend('confirmation', booking.id)}
              disabled={resendBusy !== null}
              title="Verstuur de volledige bevestiging (QR + ticket-PDF + agenda) opnieuw"
            >
              {resendBusy === 'confirmation' ? 'Versturen…' : 'Bevestiging versturen'}
            </button>
            <button
              className="resend-manage-btn"
              onClick={() => void handleResend('manage_link', booking.id)}
              disabled={resendBusy !== null}
              title="Verstuur een korte e-mail met de verzet-link, zodat de klant zelf kan herplannen"
            >
              {resendBusy === 'manage_link' ? 'Versturen…' : 'Stuur verzet-link'}
            </button>
            {booking.payment_status === 'unpaid' ? (
              <button
                className="resend-manage-btn"
                onClick={() => void handleResend('payment_request', booking.id)}
                disabled={resendBusy !== null}
                title="Verstuur een e-mail met het openstaande bedrag en een link waarmee de klant online kan betalen"
              >
                {resendBusy === 'payment_request' ? 'Versturen…' : 'Stuur betaallink'}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
      {resendNotice ? (
        <div
          className={
            resendNotice.kind === 'ok'
              ? 'resend-notice resend-notice-ok'
              : 'resend-notice resend-notice-err'
          }
        >
          {resendNotice.message}
        </div>
      ) : null}

      {cancelOpen ? (
        <CancelRefundModal
          booking={booking}
          siblingCount={siblings.length}
          onClose={(changed) => {
            setCancelOpen(false);
            if (changed) void load();
          }}
        />
      ) : null}

      {editOpen ? (
        <EditBookingModal
          booking={booking}
          onClose={(changed) => {
            setEditOpen(false);
            if (changed) void load();
          }}
        />
      ) : null}

      {markPaidOpen ? (
        <MarkPaidModal
          booking={booking}
          onClose={(changed) => {
            setMarkPaidOpen(false);
            if (changed) void load();
          }}
        />
      ) : null}

      {terminalOpen ? (
        <TerminalChargeModal
          bookingId={booking.parent_booking_id}
          bookingNumber={booking.booking_number}
          departureId={booking.id}
          onClose={(changed) => {
            setTerminalOpen(false);
            if (changed) void load();
          }}
        />
      ) : null}

      <section className="audit-section">
        <h2>Geschiedenis</h2>
        <AuditTimeline
          items={audit.map((row) => ({
            id:          row.id,
            created_at:  row.created_at,
            actor_type:  row.actor_type,
            actor_label: row.actor_label,
            action:      row.action,
            value:       row.to_value,
          }))}
        />
      </section>
    </div>
  );
}
